import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Avatar,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Search, CheckCircle, Email, Phone } from "@mui/icons-material";
import { formatCurrency, formatDate } from "@nomnom/core";
import type { User } from "../types/firestore";
import {
  buildUserRows,
  filterUserRows,
  subscribeToStudentProfiles,
  subscribeToStudentUsers,
  updateStudentUserStatus,
  type FilterValue,
  type UserRow,
} from "../services/usersService";

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [studentProfiles, setStudentProfiles] = useState<Record<string, import("../types/firestore").StudentProfile>>({});

  const [usersReady, setUsersReady] = useState(false);
  const [profilesReady, setProfilesReady] = useState(false);

  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [desiredActive, setDesiredActive] = useState(true);
  const [adminNotes, setAdminNotes] = useState("");
  const [actionDialog, setActionDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterValue>("all");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToStudentUsers(
      (nextUsers) => {
        setUsers(nextUsers);
        setUsersReady(true);
        setError(null);
      },
      (message) => {
        setUsersReady(true);
        setError(message);
      },
    );
  }, []);

  useEffect(() => {
    return subscribeToStudentProfiles(
      (nextProfiles) => {
        setStudentProfiles(nextProfiles);
        setProfilesReady(true);
      },
      (message) => {
        setProfilesReady(true);
        setError(message);
      },
    );
  }, []);

  const loading = !(usersReady && profilesReady);

  const userRows = useMemo(
    () => buildUserRows(users, studentProfiles),
    [studentProfiles, users],
  );

  const filteredUsers = useMemo(
    () => filterUserRows(userRows, filter, search),
    [filter, search, userRows],
  );

  const openUserDialog = (user: UserRow) => {
    setSelectedUser(user);
    setDesiredActive(user.active);
    setAdminNotes("");
    setActionDialog(true);
    setError(null);
    setSuccess(null);
  };

  const closeDialog = () => {
    if (actionLoading) {
      return;
    }

    setActionDialog(false);
    setSelectedUser(null);
    setAdminNotes("");
  };

  const updateUserStatus = async () => {
    if (!selectedUser) {
      return;
    }

    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await updateStudentUserStatus(selectedUser.uid, desiredActive, adminNotes);
      setSuccess(`Updated user ${selectedUser.name}.`);
      setActionDialog(false);
      setSelectedUser(null);
      setAdminNotes("");
    } catch (updateError) {
      setError(
        updateError instanceof Error
          ? updateError.message
          : "Failed to update user status.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Students</Typography>
        <Typography variant="body2" color="text.secondary">
          Live Firestore data
        </Typography>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {success && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success">{success}</Alert>
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            placeholder="Search students..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            label="Filter"
            value={filter}
            onChange={(event) => setFilter(event.target.value as FilterValue)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Users</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="verified">Verified</MenuItem>
            <MenuItem value="unverified">Unverified</MenuItem>
          </TextField>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Orders</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" align="center" py={4}>
                    No students found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.uid} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ mr: 2 }}>
                        {user.name
                          .split(" ")
                          .map((part) => part[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Joined {formatDate(user.joinedAt, "Unknown")}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                        <Email fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                        <Typography variant="body2">{user.email}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Phone fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                        <Typography variant="body2">{user.phone}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.college}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.dorm}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.totalOrders} orders</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(user.totalSpent)} spent
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.rating.toFixed(1)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Chip
                        label={user.active ? "Active" : "Inactive"}
                        color={user.active ? "success" : "default"}
                        size="small"
                      />
                      {user.verified && (
                        <Chip
                          label="Verified"
                          color="primary"
                          size="small"
                          icon={<CheckCircle />}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" onClick={() => openUserDialog(user)}>
                      Manage
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={actionDialog} onClose={closeDialog}>
        <DialogTitle>Update User</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {selectedUser?.name} ({selectedUser?.email})
          </Typography>

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={desiredActive}
                  onChange={(event) => setDesiredActive(event.target.checked)}
                />
              }
              label="Active User"
            />

            <TextField
              label="Admin Notes"
              multiline
              rows={3}
              placeholder="Optional notes for support operations"
              fullWidth
              sx={{ mt: 2 }}
              value={adminNotes}
              onChange={(event) => setAdminNotes(event.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={updateUserStatus} variant="contained" disabled={actionLoading}>
            {actionLoading ? "Updating..." : "Update User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
